/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import { motion } from 'framer-motion';
import { Layers, Check } from 'lucide-react';

const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif" };

/**
 * ConvCompatBand — Bande de réassurance fine (entre Solution et Copilots).
 * Neutralise l'objection #1 des dirigeants : "je dois changer d'outil ?".
 * Positionnement : Homly est une surcouche, vos outils restent intacts.
 * Volontairement sans logos CRM (Homly ne mentionne plus aucune intégration API).
 */
export function ConvCompatBand() {
    return (
        <section
            className="relative py-10 md:py-14 bg-[#08080f] border-y border-white/[0.04] overflow-hidden"
            data-testid="conv-compat-band"
        >
            {/* Subtle ambient glow */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[200px] bg-[#681793]/[0.05] rounded-full blur-[120px]" />
            </div>

            <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6">
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                    className="flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10"
                >
                    {/* Icon block */}
                    <div className="flex items-center gap-3 shrink-0">
                        <div className="p-2.5 rounded-xl bg-gradient-to-br from-[#681793]/20 to-[#FF914D]/15 border border-white/10">
                            <Layers className="w-5 h-5 text-white/85" strokeWidth={1.6} />
                        </div>
                        <span
                            className="text-[10px] uppercase tracking-[0.18em] font-semibold text-white/55"
                            style={DISPLAY}
                        >
                            Compatible avec vos outils
                        </span>
                    </div>

                    {/* Message */}
                    <div className="flex-1 min-w-0">
                        <h3
                            className="text-lg md:text-xl font-bold text-white leading-snug mb-1.5"
                            style={DISPLAY}
                            data-testid="conv-compat-band-title"
                        >
                            Votre logiciel reste votre logiciel.
                            <span className="text-white/55"> Homly s'ajoute par-dessus.</span>
                        </h3>
                        <p className="text-sm text-white/55 leading-relaxed">
                            Aucune migration, aucun remplacement. Homly s'installe en parallèle de vos outils actuels —
                            vous gardez vos outils, vous gagnez la vision et le pilotage.
                        </p>
                    </div>

                    {/* Reassurance pills */}
                    <div className="flex flex-wrap gap-2 md:gap-2.5 shrink-0">
                        {[
                            'Sans migration',
                            'Sans remplacement',
                            'Prise en main rapide',
                        ].map((label) => (
                            <span
                                key={label}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[11px] text-white/70 font-medium"
                                data-testid={`conv-compat-pill-${label.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                                <Check className="w-3 h-3 text-emerald-400/80" strokeWidth={2.5} />
                                {label}
                            </span>
                        ))}
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
